import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchPublishedTeaching,
  filterTeaching,
  deriveTeachingFacets,
  EMPTY_TEACHING_FILTERS,
  TEACHING_TYPES,
} from '../lib/teaching.js';
import { useAuth } from '../context/AuthContext.jsx';
import { loadWithRetry } from '../lib/loadWithRetry.js';
import TeachingCard from '../components/teaching/TeachingCard.jsx';
import TeachingFilters from '../components/teaching/TeachingFilters.jsx';
import TeachingDetailModal from '../components/teaching/TeachingDetailModal.jsx';

export default function Teaching() {
  const { t } = useTranslation();
  const { isActive } = useAuth();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState(EMPTY_TEACHING_FILTERS);
  const [detail, setDetail] = useState(null);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    loadWithRetry(fetchPublishedTeaching, { label: 'teaching' })
      .then((rows) => !cancelled && setItems(rows))
      .catch((err) => !cancelled && setError(err.message ?? String(err)));
    return () => {
      cancelled = true;
    };
  }, [retryKey]);

  // Deep link from a What's New notice: /teaching?entry=<id>.
  useEffect(() => {
    const id = searchParams.get('entry');
    if (!id || !items) return;
    const match = items.find((m) => m.id === id);
    if (match) setDetail(match);
  }, [items, searchParams]);

  const facets = useMemo(() => (items ? deriveTeachingFacets(items) : null), [items]);
  const results = useMemo(() => {
    if (!items) return [];
    return filterTeaching(items, { ...filters, query });
  }, [items, filters, query]);

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-ink-secondary">{error}</p>
        <button
          type="button"
          className="btn-primary mt-6"
          onClick={() => setRetryKey((k) => k + 1)}
        >
          {t('common.tryAgain')}
        </button>
      </div>
    );
  }
  if (!items || !facets) {
    return (
      <div role="status" aria-live="polite" className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center text-ink-secondary">
        {t('common.loading')}
      </div>
    );
  }

  const activeCount = (filters.level ? 1 : 0) + (filters.language ? 1 : 0);

  return (
    <div className="mx-auto max-w-shell px-6 pb-20 pt-28">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="section-label">{t('home.tiles.teaching.title')}</div>
          <h1 className="font-serif text-4xl font-semibold text-cream">
            {t('teaching.heading')}
          </h1>
          <p className="mt-2 max-w-2xl text-ink-secondary">
            {t('teaching.subheading', { count: items.length })}
          </p>
        </div>
        {isActive && (
          <Link to="/teaching/new" className="btn-primary">
            {t('teaching.add')}
          </Link>
        )}
      </header>

      <div className="mb-6 flex flex-wrap gap-2 border-b border-white/10 pb-3">
        <TypeTab active={filters.type === ''} label={t('teaching.types.all')} count={items.length} onClick={() => setFilters({ ...filters, type: '' })} />
        {TEACHING_TYPES.map((type) => {
          const facet = facets.types.find((x) => x.type === type);
          const count = facet?.count ?? 0;
          if (count === 0) return null;
          return (
            <TypeTab
              key={type}
              active={filters.type === type}
              label={t(`teaching.types.${type}`)}
              count={count}
              onClick={() => setFilters({ ...filters, type })}
            />
          );
        })}
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-navy-base/50 p-3 backdrop-blur">
        <input
          type="search"
          placeholder={t('teaching.searchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="min-w-0 flex-1 rounded-md border border-white/10 bg-navy-deep/60 px-4 py-2 text-cream placeholder:text-ink-muted focus:border-gold/50"
        />
        <button
          type="button"
          className="btn-ghost text-sm md:hidden"
          onClick={() => setShowFiltersMobile((s) => !s)}
        >
          {t('common.filter')}
          {activeCount > 0 && (
            <span className="ml-1 rounded-full bg-gold/20 px-1.5 text-xs text-gold-light">{activeCount}</span>
          )}
        </button>
      </div>

      <p className="mb-4 text-sm text-ink-secondary">
        {t('teaching.resultCount', { count: results.length })}
      </p>

      <div className="grid gap-8 md:grid-cols-[260px_1fr]">
        <div className={`md:block ${showFiltersMobile ? 'block' : 'hidden'}`}>
          <TeachingFilters
            facets={facets}
            filters={filters}
            onChange={setFilters}
            onClear={() => setFilters({ ...filters, level: '', language: '' })}
            activeCount={activeCount}
          />
        </div>

        <div>
          {results.length === 0 ? (
            <p className="rounded-lg border border-white/10 bg-navy-base/40 p-10 text-center text-ink-secondary">
              {items.length === 0 ? t('teaching.emptyAll') : t('teaching.empty')}
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {results.map((m) => (
                <TeachingCard key={m.id} item={m} onOpen={setDetail} />
              ))}
            </div>
          )}
        </div>
      </div>

      <TeachingDetailModal
        item={detail}
        onClose={() => setDetail(null)}
        onDeleted={(id) => setItems((prev) => prev?.filter((m) => m.id !== id) ?? prev)}
      />
    </div>
  );
}

function TypeTab({ active, label, count, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm transition ${
        active
          ? 'border-gold/60 bg-navy-mid/60 text-gold-light'
          : 'border-white/10 bg-navy-deep/40 text-ink-secondary hover:border-gold/30'
      }`}
    >
      {label}
      <span className="rounded-full bg-navy-deep/60 px-2 text-[0.7rem] text-ink-muted">{count}</span>
    </button>
  );
}
